import { isNullish } from "@tinyurldev/tu-ui-vue";
import { capitalize } from "lodash-es";

import type { InvoiceItemGroupData, InvoiceItemEntryData } from "@Modules/billing/Data/Models/InvoiceModel";
import INVOICE_ITEM_TYPE from "@Constants/invoice-item-type";
import { logError } from "@Helpers/logger";

/**
 * One part of an invoice's description: what kind of charge it is, and what it charged for.
 */
export interface DescriptionSegment {
  id: string
  label: string
  value: string
}

/**
 * Translates a key into the wording the segment is displayed with.
 */
export type Translate = (key: string, ...rest: unknown[]) => string

/**
 * The wording each renewal interval is named with in a plan's title.
 */
const INTERVAL_LABELS: Record<string, string> = {
  monthly: "billing.interval.monthly",
  yearly: "billing.interval.annual",
};

/**
 * The label each invoice item type is displayed under, in the order the segments appear in.
 */
const DESCRIPTION_SEGMENT_LABELS: { type: number; labelKey: string }[] = [
  { type: INVOICE_ITEM_TYPE.SUBSCRIPTION, labelKey: "settings_billing_page.transaction.table.subscription" },
  { type: INVOICE_ITEM_TYPE.PRORATION, labelKey: "settings_billing_page.transaction.table.proration" },
  { type: INVOICE_ITEM_TYPE.OVERAGE, labelKey: "settings_billing_page.transaction.table.overage" },
  { type: INVOICE_ITEM_TYPE.ADD_ON, labelKey: "settings_billing_page.transaction.table.add_on" },
  { type: INVOICE_ITEM_TYPE.ONE_TIME, labelKey: "settings_billing_page.transaction.table.one_time" },
  { type: INVOICE_ITEM_TYPE.DOMAIN_PURCHASE, labelKey: "settings_billing_page.transaction.table.domain_purchase" },
  { type: INVOICE_ITEM_TYPE.DOMAIN_RENEWAL, labelKey: "settings_billing_page.transaction.table.domain_renewal" },
];

/**
 * Names a plan the way the rest of the app names it, from our own catalog: its title and
 * the interval it renews on, such as "Pro 50K Annual".
 *
 * Answers an empty string when the catalog does not name the plan, leaving the caller to
 * fall back on what the invoice item itself carries.
 */
function formatSubscriptionName(entry: InvoiceItemEntryData, translate: Translate): string {
  if (isNullish(entry.original_name)) {
    return "";
  }

  const titleKey = `${entry.original_name}.title`;
  const title = translate(`subscriptions:${titleKey}`);

  // i18next answers a missing key with the key itself, minus its namespace.
  if (title === titleKey) {
    return "";
  }

  const intervalKey: string | undefined = INTERVAL_LABELS[entry.interval ?? ""];

  return isNullish(intervalKey) ? title : `${title} ${capitalize(translate(intervalKey))}`;
}

/**
 * Drops the group's own label from an item that repeats it, so a domain purchase reads as
 * "Domain Purchase: foo.xyz, bar.xyz" instead of
 * "Domain Purchase: Domain Purchase: foo.xyz, Domain Purchase: bar.xyz".
 */
function removeRepetitiveGroupFromValue(text: string, groupLabel?: string | null): string {
  if (isNullish(groupLabel) || !text.startsWith(`${groupLabel}:`)) {
    return text;
  }

  return text.slice(groupLabel.length + 1).trim();
}

/**
 * Builds one description segment out of an items_breakdown group.
 */
function buildSegment(
  group: InvoiceItemGroupData | undefined,
  label: string | null | undefined,
  type: number,
  translate: Translate,
): DescriptionSegment {
  const items = group?.entries ?? [];

  return {
    id: `item-type-${type}`,
    label: isNullish(label) ? "" : `${label}:`,
    value: items
      .map((item) => formatSubscriptionName(item, translate) || item.name?.trim() || item.description?.trim() || "")
      .map((text) => removeRepetitiveGroupFromValue(text, group?.label))
      .filter(Boolean)
      .join(", "),
  };
}

/**
 * Builds the description segments for the transaction table, one per item type the invoice
 * charges for. The backend groups the items; this only names the groups and orders them.
 *
 * Answers an empty array when the breakdown is missing or cannot be read.
 */
export default function buildDescriptionSegments(
  itemsBreakdown: InvoiceItemGroupData[] | null | undefined,
  translate: Translate,
): DescriptionSegment[] {
  if (isNullish(itemsBreakdown)) {
    return [];
  }

  try {
    const labelledTypes = DESCRIPTION_SEGMENT_LABELS.map(({ type }) => type);

    const labelled = DESCRIPTION_SEGMENT_LABELS.map(({ type, labelKey }) => {
      const breakdownGroup = itemsBreakdown.find((itemGroup) => itemGroup.type === type);

      return buildSegment(breakdownGroup, translate(labelKey), type, translate);
    });

    // A type we have no label of our own for is shown under the label the API sent, so a
    // charge the backend starts sending is never silently missing from the description.
    const unlabelled = itemsBreakdown
      .filter((itemGroup) => !labelledTypes.includes(itemGroup.type))
      .map((itemGroup) => buildSegment(itemGroup, itemGroup.label, itemGroup.type, translate));

    return [...labelled, ...unlabelled].filter((segment) => segment.value !== "");
  } catch (error) {
    logError(error);

    return [];
  }
}
